// ─── React & Dependencies ───────────────────────────────────────
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import { useEffect, useState, useCallback } from "react";

// ─── Components ──────────────────────────────────────────────────
import TransactionList from "../transactionList";

// ─── Hooks & Providers ──────────────────────────────────────────
import { useWallet } from "../../../../providers/wallet/wallet.provider";
import classNames from "classnames";

import PrimaryButton, {
  PrimaryButtonSize,
  PrimaryButtonType,
} from "../../../../components/common/PrimaryButton";
import BulkList from "../bulkList";

interface WalletListProps {
  containerRef?: React.RefObject<HTMLDivElement>;
  hideWithdrawTab?: boolean;
}

const WalletList = ({ containerRef, hideWithdrawTab }: WalletListProps) => {
  // ─── Hooks & State ────────────────────────────────────────────
  const { t } = useTranslation();
  const [showMobileView, setShowMobileView] = useState(false);

  const withdrawalHistory = useWallet((state) => state.withdrawalHistory);

  const manualWithdrawFeatureEnabled = !hideWithdrawTab;

  // ─── Effects ──────────────────────────────────────────────────
  // Set mobile view based on screen size
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      setShowMobileView(width < 992 || (width >= 1000 && height >= 1900));
    };
    handleResize(); // Set initial value
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (hideWithdrawTab) handleTabClick("transaction");
  }, [hideWithdrawTab]);

  // ─── Wallet State from Provider ──────────────────────────────
  const activeTab = useWallet((state) => state.activeTab);
  const setActiveTab = useWallet((state) => state.setActiveTab);

  const handleTabClick = useCallback(
    (tab: "transaction" | "bulk") => {
      setActiveTab(tab);
    },
    [setActiveTab]
  );

  const navigateToBulkWithdrawal = useCallback(() => {
    // navigate(ROUTES.WALLET_WITHDRAWAL);
  }, []);

  // ─── Render Logic ─────────────────────────────────────────────

  return (
    <div className={styles["list_container"]}>
      {manualWithdrawFeatureEnabled && (
        <>
          {withdrawalHistory?.length > 0 && (
            <div
              className={classNames(
                styles["listing_button_container"],
                styles["mob_visible"]
              )}>
              <PrimaryButton
                id="contract_details_add_adress_confirm"
                buttonType={PrimaryButtonType.PRIMARY_LIGHT}
                size={PrimaryButtonSize.MEDIUM}
                onClick={navigateToBulkWithdrawal}>
                <p
                  className={classNames(
                    styles.button_text,
                    styles.buttonActive
                  )}>
                  {t("balance_tabs_bulk_listing")}
                </p>
              </PrimaryButton>
            </div>
          )}
        </>
      )}

      {/* ─── Header Section ───────────────────────────────────── */}
      <div className={styles["header"]}>
        <div className={styles["title_cont"]}>
          <div className={styles["title_tabs"]}>
            <div
              onClick={() => handleTabClick("transaction")}
              className={styles["tab_wrapper"]}>
              <p
                className={classNames(styles["title"], {
                  [styles["active"]]: activeTab === "transaction",
                })}>
                {t("balance_transaction_log")}
              </p>
              {activeTab === "transaction" && (
                <div className={styles["blue_line"]} />
              )}
            </div>
            {manualWithdrawFeatureEnabled && (
              <div
                onClick={() => handleTabClick("bulk")}
                className={classNames(
                  styles["tab_wrapper"],
                  styles["cursor_pointer"]
                )}>
                <p
                  className={classNames(styles["title"], {
                    [styles["active"]]: activeTab === "bulk",
                  })}>
                  {t("balance_tabs_bulk_listing")}
                </p>
                {activeTab === "bulk" && (
                  <div className={styles["blue_line"]} />
                )}
              </div>
            )}
          </div>
        </div>
        <div className={styles["filer_container"]}></div>
      </div>

      {/* ─── Content Section ──────────────────────────────────── */}
      <div className={styles["sub_container"]}>
        {activeTab === "transaction" ? (
          <TransactionList
            hideWithdrawTab={!!hideWithdrawTab}
            showMobileView={showMobileView}
            containerRef={containerRef}
          />
        ) : (
          <BulkList showMobileView={showMobileView} />
        )}
      </div>
    </div>
  );
};

export default WalletList;
